import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';

import CustomCheckbox from '../../../../components/shared/customCheckbox/customCheckbox';
import { DISABLED_NOTIFICATIONS, NOTIFICATION_SETTINGS } from '../../../../constants/notificationConstants';
import { getNotificationSettings, updateNotificationSettings } from '../../../../redux/actions/notificationActions';
import { selectCurrentUser } from '../../../../selectors/authSelectors';
import { selectOptedOutNotifications } from '../../../../selectors/notificationSelectors';

import styles from './NotificationSettings.module.scss';

const NOTIFICATION_KEY_MAP = {
  referral_status_updates: 'referralStatusUpdates',
  application_status_updates: 'applicationStatusUpdates',
  reward_updates: 'rewardUpdates',
  leaderboard_updates: 'leaderboardUpdates',
  recommendations: 'recommendations',
  product_updates: 'productUpdates',
  account_management: 'accountManagement',
  company_related_content: 'companyRelatedContent',
  referral_round_up: 'referralRoundUp',
};

const NotificationSettings = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);

  const notificationsEnabled = useSelector((state) => state.notificationReducer.notificationsEnabled);
  const optOutNotifications = useSelector(selectOptedOutNotifications);

  const checkDisable = (key) => !!(DISABLED_NOTIFICATIONS.includes(key) || optOutNotifications.includes(key));

  const notificationTypes = useMemo(
    () => [
      { key: 'sms', label: t('notificationSettings.sms') },
      { key: 'email', label: t('notificationSettings.email') },
    ],
    [t]
  );

  const handleCheckboxChange = useCallback((e, key, id) => {
    dispatch(updateNotificationSettings(id, { [key]: e.currentTarget.checked }));
  }, []);

  useEffect(() => {
    if (user?.id) {
      dispatch(getNotificationSettings(user?.id));
    }
  }, [dispatch, user?.id]);

  const getNotificationHeading = (key) => {
    const translationKey = NOTIFICATION_KEY_MAP[key];
    return translationKey ? t(`notificationSettings.${translationKey}`) : key;
  };

  return user ? (
    <Formik>
      <Form>
        <div className={styles.notificationHeading}>{t('notificationSettings.title')}</div>
        <div className={styles.notificationColumn}>
          <div className={styles.notificationColumnText}>{t('notificationSettings.notificationType')}</div>
          <div className={styles.notificationColumnOption}>
            {notificationTypes.map((type) => (
              <div key={type.key} className={styles.notificationColumnText}>
                {type.label}
              </div>
            ))}
          </div>
        </div>
        {NOTIFICATION_SETTINGS.map((notification) => (
          <React.Fragment key={notification.key}>
            <div className={styles.horizontalLine} />
            <div className={styles.notificationContent}>
              <div className={styles.notificationDescription}>
                <div className={styles.notificationDescriptionText}>{getNotificationHeading(notification.key)}</div>
                <div className={styles.notificationColumnOption}>
                  {notificationTypes.map((type) => (
                    <div key={type.key} className={styles.checkboxTextCont}>
                      <CustomCheckbox
                        id={type.key}
                        checked={notificationsEnabled[`${notification.key}_${type.key}`] || false}
                        disabled={checkDisable(`${notification.key}_${type.key}`)}
                        onCheckboxChange={(e) => handleCheckboxChange(e, [`${notification.key}_${type.key}`], user?.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
      </Form>
    </Formik>
  ) : null;
};

export default NotificationSettings;
